import { InteractivityEditorService } from './../../../services/interactivity-editor.service';
import { Subscription, combineLatest, ReplaySubject } from 'rxjs';
import { InteractivityActionService } from '../../../services/interactivity-action.service';
import { CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { IProcedure, IAction } from '../../../redux/timeline-data.model';
import { Component, Input, ViewChildren, QueryList, Output, EventEmitter, SimpleChanges, OnInit, OnChanges, AfterViewInit, OnDestroy } from '@angular/core';
import { bbGenerateShortId } from '../../../../../../shared/utils/bbGenerateShortId.util';
import { bbDeepCopy } from '../../../../../../shared/utils/bbDeepCopy.util';
import { debounceTime } from 'rxjs/operators';

@Component({
	selector: 'procedure',
	templateUrl: './procedure.component.html',
	styleUrls: ['./procedure.component.scss']
})

export class ProcedureComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@ViewChildren('ActionsContainer') actionDropAreas !: QueryList<CdkDropList>;
	@ViewChildren('ElseActionsContainer') elseActionDropAreas !: QueryList<CdkDropList>;

	@Input() handlerName: string;
	@Input() procedure: IProcedure;
	@Input() stateTrackingUid: string;
	@Output() delete: EventEmitter<IProcedure>;
	@Output() procedureChanged: EventEmitter<string>;

	editMode = false;
	expressionRows: any[] = [];
	abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
	public expressionInvalid: boolean;

	actionDropsites: CdkDropList[];
	private subscriptions = new Subscription();

	private expressionRegex: RegExp;
	private expressionSubject: ReplaySubject<string>;

	constructor(
		private interactivityActionService: InteractivityActionService,
		public interactivityEditorService: InteractivityEditorService
	) {
		this.actionDropsites = [];
		this.delete = new EventEmitter<IProcedure>();
		this.procedureChanged = new EventEmitter<string>();

		this.subscriptions.add(
			this.interactivityActionService.actionDropsitesSubject.subscribe((actionDropSites: CdkDropList[]) => {
				this.actionDropsites = actionDropSites;
			})
		);
		this.expressionSubject = new ReplaySubject<string>(1);
		this.expressionRegex = /((?:\(*[A-Z]{1} (?:AND|OR){1} \(*[A-Z]{1}[\)]*)+)( (?:AND|OR) \(*[A-Z]{1}\)*)*/;
	}

	ngOnInit() {
		this.subscriptions.add(
			this.expressionSubject.pipe(
				debounceTime(500)
			).subscribe((expression: string) => {
				this.expressionInvalid = !this.expressionValid(expression);
			})
		);
		this.checkProcedure();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.procedure) {
			this.updateExpressionRows();
			this.checkProcedure();
		}
	}

	checkProcedure() {
		if (this.procedure) {
			if (!this.procedure.id) {
				this.procedure.id = bbGenerateShortId();
			}
			if (this.procedure.condition && this.procedure.condition.expression) {
				this.expressionInvalid = this.procedure.condition.variables.length && !this.expressionValid(this.procedure.condition.expression);
			}
		}
	}

	expressionValid(expression: string) {

		let matches: RegExpExecArray;

		// Remove multiple spaces
		expression = expression.replace(/  +/gi, ' ');
		// Remove spaces from '( ' and ' )' in the condition
		expression = expression.replace(/(\() +| +(\))/gi, '$1$2');
		const correctParentheses = expression.split('(').length === expression.split(')').length;
		expression = expression.replace(/[\(\)]/gi, '');

		if (correctParentheses && (matches = this.expressionRegex.exec(expression)) !== null) {
			if (matches[0] === expression) {
				return true;
			} else {
				return false;
			}
		} else {
			return expression.length === 1 && /[A-Z]/.test(expression) && correctParentheses;
		}
	}

	expressionValueChange(value: string) {
		this.expressionSubject.next(value);
	}

	updateExpressionRows() {
		if (this.procedure && this.procedure.condition && this.procedure.condition.expression) {
			this.expressionRows = this.makeExpressionRows(this.procedure.condition.expression);
		} else {
			this.expressionRows = [];
		}
	}

	makeExpressionRows(expression: string) {
		const parts: any[] = [];
		const andParts = expression.split('AND');
		andParts.forEach((andPart, i) => {
			const orParts = andPart.split('OR');
			if (i === 0) {
				parts.push(this.makeExpressionPart(orParts[0], 'IF'));
			} else {
				parts.push(this.makeExpressionPart(orParts[0], 'AND'));
			}

			orParts.forEach((orPart, j) => {
				if (j > 0) {
					parts.push(this.makeExpressionPart(orPart, 'OR'));
				}
			});
		});
		return parts;
	}

	makeExpressionPart(part: string, o: 'IF' | 'AND' | 'OR') {
		const p1 = part.replace(/[\sa-zA-Z\)]/g, '');
		const p2 = part.replace(/[\sa-zA-Z\(]/g, '');
		const letter = part.replace(/[\s\(\)]/g, '');
		const letterIndex = this.abc.indexOf(letter);
		return {
			o,
			p1,
			p2,
			letter,
			condition: this.procedure.condition.variables[letterIndex]
		};
	}

	getLRValue(val: any): string {
		if (this.hasDataSource(val) && val.datasource.defaultValue !== 'undefined') {
			return val.datasource.defaultValue;
		}
		return val;
	}

	hasDataSource(val: any): boolean {
		let ret = false;
		if (typeof val === 'object' && val && typeof val.datasource !== 'undefined') {
			ret = true;
		}
		return ret;
	}

	ngAfterViewInit() {
		setTimeout(() => {
			const actionDropsites: CdkDropList[] = this.actionDropAreas.toArray().concat(this.elseActionDropAreas.toArray());
			this.interactivityActionService.addActionDropSites(actionDropsites);

			if (this.subscriptions.closed) {
				return;
			}
			this.subscriptions.add(
				combineLatest([this.actionDropAreas.changes, this.elseActionDropAreas.changes])
					.subscribe((r) => {
						setTimeout(() => {
							if (this.subscriptions.closed) {
								return;
							}
							const actionDropsites2: CdkDropList[] = this.actionDropAreas.toArray().concat(this.elseActionDropAreas.toArray());
							this.interactivityActionService.addActionDropSites(actionDropsites2);
						});
					}));
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	actionTrackBy(i: number, item: IAction) {
		return item.id || this.stateTrackingUid + '_a' + i;
	}

	elseActionTrackBy(i: number, item: IAction) {
		return item.id || this.stateTrackingUid + '_ea' + i;
	}

	expressionTrackBy(i: number, item: any) {
		return item.id || this.stateTrackingUid + '_cr' + i;
	}

	drop(event: CdkDragDrop<string[]>) {
		if (event.previousContainer === event.container) {
			if (event.previousIndex !== event.currentIndex) {
				moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
				this.dispatchChange('Changed actions order');
			}
		} else {
			if (
				event.previousContainer.element.nativeElement.getAttribute('handlername') !== event.container.element.nativeElement.getAttribute('handlername')
			) {
				// when in another handler, we make a copy with a new id for now.
				const action = event.previousContainer.data[event.previousIndex];
				const newAction: any = bbDeepCopy(action);
				newAction.id = bbGenerateShortId();
				event.previousContainer.data[event.previousIndex] = newAction;
			}
			transferArrayItem(event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex);
			// console.log( 'MOVED TO OTHER LIST event: ', event);
			// console.log( 'MOVED TO OTHER LIST from: ', event.previousContainer.data);
			// console.log( 'MOVED TO OTHER LIST to: ', event.container.data);
			if (this.procedure.type === 'empty') {
				this.procedure.type = 'simple';
			}
			this.dispatchChange('Changed actions order');
		}
	}

	toggleEditMode(val: boolean) {
		this.editMode = val;
	}

	entered(event: CdkDragEnter<string[]>) {
	}

	exited(event: CdkDragExit<string[]>) {
	}

	deleteProcedure(): void {
		this.delete.emit(this.procedure);
	}

	// addAction(): void {
	// 	this.procedure.actions.push({
	// 		entity: 'TESTENTITY',
	// 		command: 'TESTCOMMAND',
	// 		parameters: []
	// 	});
	// 	this.dispatchChange( 'Added action' );
	// }
	// addElseAction(): void {
	// 	this.procedure.else_actions.push({
	// 		entity: 'TESTENTITY',
	// 		command: 'TESTCOMMAND',
	// 		parameters: []
	// 	});
	// 	this.dispatchChange( 'Added else-action' );
	// }

	removeAction(action: IAction): void {
		const index: number = this.procedure.actions.indexOf(action, 0);
		if (index > -1) {
			this.procedure.actions.splice(index, 1);
			if (this.procedure.type !== 'conditional' && !this.procedure.actions.length && !this.procedure.else_actions.length) {
				this.deleteProcedure();
			} else {
				this.dispatchChange('Removed action');
			}
		}
	}

	removeElseAction(action: IAction): void {
		const index: number = this.procedure.else_actions.indexOf(action, 0);
		if (index > -1) {
			this.procedure.else_actions.splice(index, 1);
			this.dispatchChange('Removed else-action');
		}
	}

	dispatchChange(msg: string): void {
		this.updateExpressionRows();
		this.procedureChanged.emit(msg);
	}

	actionChanged(): void {
		this.dispatchChange('Changed action');
	}

	onDuplicateAction(action) {
		const index = this.procedure.actions.indexOf(action, 0);
		const actionCopy = bbDeepCopy(action);
		actionCopy.id = bbGenerateShortId();
		this.procedure.actions.splice(index, 0, actionCopy);
		this.dispatchChange('Duplicated action');
	}

	actionAdded(msg?: string): void {
		this.dispatchChange(msg || 'Added action');
	}

	conditionChanged(): void {
		this.dispatchChange('Changed condition');
	}
}
